import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import { openAccountModal } from '@/features/user/accountModalSlice';
import { rewardFromLabel } from '../format';
import { useGuestProgram, useLoyaltyApiPresent } from '../useGuestLoyalty';
import '../loyalty.css';

interface Props {
  restaurantUuid: string;
}

function Teaser({ restaurantUuid }: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const program = useGuestProgram(restaurantUuid);
  if (!program?.enabled) return null;
  const { points_per_euro: perEuro, points_per_visit: perVisit } = program;
  // A program that pays nothing has nothing to announce.
  if (perEuro <= 0 && perVisit <= 0) return null;

  const earn =
    perEuro > 0 && perVisit > 0
      ? t('loyalty.guest.earnBoth', { n: perEuro, m: perVisit })
      : perEuro > 0
        ? t('loyalty.guest.earnEuro', { n: perEuro })
        : t('loyalty.guest.earnVisit', { n: perVisit });
  const reward = program.cheapest_reward ?? null;
  const text = [
    t('loyalty.guest.teaser', { earn }),
    reward ? rewardFromLabel(t, reward, i18n.language) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      data-testid="guest-loyalty-teaser"
      className="loyalty-scope mb-3 flex min-h-[44px] items-center gap-2 rounded-btn-sm border-2 border-solid border-ink bg-cream ps-3 pe-2 font-mono text-xs text-ink"
    >
      <span aria-hidden="true">🎁</span>
      {/* One line whatever the width: a 360px phone gets the start of it, the
          rest is in the title (and read in full by a screen reader). */}
      <p className="m-0 min-w-0 flex-1 truncate" title={text}>
        {text}
      </p>
      <button
        type="button"
        onClick={() =>
          dispatch(openAccountModal({ mode: 'login', redirectTo: `${pathname}${search}` }))
        }
        data-testid="guest-loyalty-teaser-login"
        className="min-h-[40px] flex-none border-0 bg-transparent p-0 font-mono text-xs font-bold text-ink underline"
      >
        {t('loyalty.guest.login')}
      </button>
    </div>
  );
}

/**
 * SC-1640 — on the menu, where people decide, a guest is told in one line that the
 * restaurant runs a loyalty program, how points are earned and what the cheapest
 * reward is — with the login one tap away. Nothing for a logged-in customer (the
 * site menu has « Ma carte fidélité » for them), nothing when the program is off
 * or the call fails. Behind the API gate: some test stores have no loyalty reducer.
 */
export default function GuestLoyaltyTeaser(props: Props) {
  if (!useLoyaltyApiPresent()) return null;
  return <Teaser {...props} />;
}
